import { useLayoutEffect, useRef, type ReactNode } from 'react'
import { cx } from './cx.ts'
import { IconTile, type Area } from './layout.tsx'

// Spelled out so Tailwind sees every class.
const PILL_ON: Record<Area, string> = {
  sell: 'border-sell/40 bg-sell-wash text-sell',
  people: 'border-people/40 bg-people-wash text-people',
  stock: 'border-stock/40 bg-stock-wash text-stock',
  buy: 'border-buy/40 bg-buy-wash text-buy',
  insight: 'border-insight/40 bg-insight-wash text-insight',
  kitchen: 'border-kitchen/40 bg-kitchen-wash text-kitchen',
  neutral: 'border-neutral/40 bg-neutral-wash text-neutral',
  take: 'border-take/40 bg-take-wash text-take',
}

/**
 * One choice in a row of filters (order type, table area, kitchen station): a rounded pill,
 * a `role="tab"` for a `role="tablist"` around it. A pill that stands for a kind of thing
 * carries that kind's icon on a tile of its colour and takes that tint when chosen; one
 * without (such as "All") takes the app's own indigo.
 */
export function FilterPill({
  selected,
  onSelect,
  area,
  icon,
  count = 0,
  attention,
  children,
}: {
  selected: boolean
  onSelect: () => void
  area?: Area
  /** Drawn on a tile of the area's colour. */
  icon?: ReactNode
  /** Shown after the label when above zero. */
  count?: number
  /** A green dot with this accessible name ("ready"). */
  attention?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={onSelect}
      className={cx(
        'flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border text-sm font-medium transition-colors',
        icon ? 'pr-3.5 pl-1' : 'px-3.5',
        selected
          ? area
            ? cx('font-semibold', PILL_ON[area])
            : 'border-primary/40 bg-primary-light font-semibold text-primary'
          : 'border-border bg-surface text-muted hover:bg-soft hover:text-ink',
      )}
    >
      {icon && (
        <IconTile area={area} size="sm" className="rounded-full">
          {icon}
        </IconTile>
      )}
      {children}
      {count > 0 && <span className="tabular text-xs font-semibold">{count}</span>}
      {attention && <span aria-label={attention} className="size-2 rounded-full bg-success" />}
    </button>
  )
}

/**
 * A row of `FilterPill`s; it scrolls sideways on a narrow screen rather than wrapping, with
 * no scroll bar under it (the cut-off pill at the edge shows there is more).
 */
export function FilterPills({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: ReactNode
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cx(
        'flex gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {children}
    </div>
  )
}

/** A switch between two or three views of one list (Open / Paid today). */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string
  value: T
  options: readonly { value: T; label: ReactNode }[]
  onChange: (value: T) => void
  className?: string
}) {
  const row = useRef<HTMLDivElement>(null)
  const card = useRef<HTMLSpanElement>(null)
  const placed = useRef(false)
  // One card under the chosen option, moved to it rather than redrawn. Its place and width
  // are the chosen button's own: a long label ("Out of stock") makes its button wider than
  // an equal share of the row, so a share would leave the card under the wrong words.
  useLayoutEffect(() => {
    const el = row.current
    if (!el) return
    // The first placement and a resize jump; only a change of choice slides.
    const place = (jump: boolean) => {
      const chosen = el.querySelector<HTMLElement>('[aria-selected="true"]')
      const c = card.current
      if (!chosen || !c) return
      if (jump) c.style.transition = 'none'
      c.style.width = `${chosen.offsetWidth}px`
      c.style.transform = `translateX(${chosen.offsetLeft - 3}px)`
      if (jump) {
        void c.offsetWidth
        c.style.transition = ''
      }
    }
    place(!placed.current)
    placed.current = true
    if (typeof ResizeObserver === 'undefined') return
    const resized = new ResizeObserver(() => place(true))
    resized.observe(el)
    return () => resized.disconnect()
  }, [value, options.length])
  return (
    <div
      ref={row}
      role="tablist"
      aria-label={label}
      className={cx('relative flex gap-0.5 rounded-xl bg-soft p-[3px]', className)}
    >
      <span
        ref={card}
        aria-hidden
        className="absolute inset-y-[3px] left-[3px] rounded-lg bg-surface shadow-card transition-transform duration-200 ease-out"
      />
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
          className={cx(
            'relative h-8 flex-1 basis-0 whitespace-nowrap rounded-lg px-3.5 text-sm transition-colors',
            value === o.value ? 'font-semibold text-ink' : 'font-medium text-muted hover:text-ink',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}
